import { useMemo, useState } from 'react';
import {
  Modal,
  Select,
  Button,
  Form,
  Input,
  List,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Skeleton,
  Empty,
  message,
  Divider,
  Tooltip,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeclarationCase } from '@/api/cases';
import {
  listCaseAssignments,
  addCaseAssistant,
  removeCaseAssistant,
  reassignCase,
} from '@/api/assignments';
import { listUsers } from '@/api/users_admin';
import { getMyProfile } from '@/api/me';
import { useAuth } from '@/hooks/useAuth';

/**
 * S20-C0 — 申报案"指派 / 改派" Modal。
 *
 * 数据流:
 *   GET /declaration-cases/{id}/assignments  → owner + assistants
 *   POST /…/{id}/reassign                    → 改派 owner   (tenant_admin / admin / platform_operator)
 *   POST /…/{id}/assignments                 → 加 assistant (owner-only)
 *   DELETE /…/{id}/assignments/{account_id}  → 移 assistant
 *
 * 前端权限(UX 装饰;真守卫在后端 require_permission):
 *   - 改派按钮:仅 tenant_admin / platform_operator
 *   - 加/移 assistant:owner-only(case.owner_account_id === currentUser.account_id 时显示)
 *     tenant_admin / platform_operator 也允许
 */
export interface AssignmentModalProps {
  open: boolean;
  case_: DeclarationCase | null;
  onClose: () => void;
}

const ELEVATED_ROLES = new Set(['tenant_admin', 'platform_operator', 'admin']);

export function AssignmentModal({ open, case_, onClose }: AssignmentModalProps) {
  const { role } = useAuth();
  const qc = useQueryClient();
  const caseId = case_?.id ?? '';

  // 当前账号 id — 用于判 owner-only(useAuth 只暴露 username/role,这里走 /me/profile)
  const meQuery = useQuery({
    queryKey: ['me', 'profile-for-assignment'],
    queryFn: () => getMyProfile(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const myAccountId = meQuery.data?.account_id ?? null;

  // 当前案的 owner + assistants
  const assignmentsQuery = useQuery({
    queryKey: ['case-assignments', caseId],
    queryFn: () => listCaseAssignments(caseId),
    enabled: open && !!caseId,
    staleTime: 10_000,
  });

  // 候选账号
  const usersQuery = useQuery({
    queryKey: ['cases-assignment-modal', 'users'],
    queryFn: () => listUsers({ page: 1, page_size: 100 }),
    enabled: open,
    staleTime: 30_000,
  });

  const userOptions = useMemo(() => {
    const allowedRoles = new Set(['staff', 'tenant_admin', 'manager', 'platform_operator']);
    return (usersQuery.data?.items ?? [])
      .filter((u) => allowedRoles.has(u.role))
      .map((u) => ({ value: u.id, label: u.username, role: u.role }));
  }, [usersQuery.data]);

  const userNameOf = (id: string | null | undefined): string => {
    if (!id) return '—';
    const hit = userOptions.find((o) => o.value === id);
    return hit?.label ?? id.slice(0, 8) + '…';
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['case-assignments', caseId] });
    qc.invalidateQueries({ queryKey: ['cases'] });
  };

  // === Mutations ===
  const reassignMut = useMutation({
    mutationFn: (vars: { owner_account_id: string; note?: string }) =>
      reassignCase(caseId, vars),
    onSuccess: () => {
      message.success('已改派负责人');
      setReassignOpen(false);
      reassignForm.resetFields();
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '改派失败'),
  });

  const addAssistantMut = useMutation({
    mutationFn: (vars: { account_id: string; notes?: string }) =>
      addCaseAssistant(caseId, vars),
    onSuccess: () => {
      message.success('已添加协助者');
      addAssistantForm.resetFields();
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '添加失败'),
  });

  const removeAssistantMut = useMutation({
    mutationFn: (accountId: string) => removeCaseAssistant(caseId, accountId),
    onSuccess: () => {
      message.success('已移除协助者');
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '移除失败'),
  });

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignForm] = Form.useForm<{ owner_account_id: string; note?: string }>();
  const [addAssistantForm] = Form.useForm<{ account_id: string; notes?: string }>();

  // === 权限分级(UX) ===
  const isElevated = !!role && ELEVATED_ROLES.has(role);
  const isOwner = !!myAccountId && case_?.owner_account_id === myAccountId;
  const canReassign = isElevated;
  const canManageAssistants = isElevated || isOwner;

  const data = assignmentsQuery.data;
  const ownerRow = data?.items.find(
    (r) => r.account_id === data.primary_account_id,
  );
  const assistantRows = (data?.items ?? []).filter(
    (r) => r.account_id !== data?.primary_account_id,
  );

  return (
    <>
      <Modal
        title="指派 / 改派"
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        destroyOnClose
      >
        {!case_ ? null : (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                申报案
              </Typography.Text>
              <div>
                <Typography.Text strong>{case_.subject_name || case_.subject_id}</Typography.Text>
                <Tag style={{ marginLeft: 8 }}>{case_.status}</Tag>
              </div>
            </div>

            <Divider style={{ margin: '4px 0' }} />

            {assignmentsQuery.isLoading && <Skeleton active />}
            {assignmentsQuery.isError && (
              <Typography.Text type="danger">加载分配失败</Typography.Text>
            )}

            {data && (
              <>
                {/* Owner 行 */}
                <div>
                  <Typography.Text strong style={{ fontSize: 'var(--font-size-sm)' }}>
                    负责人 (Owner)
                  </Typography.Text>
                  <div
                    style={{
                      marginTop: 6,
                      padding: '8px 12px',
                      background: 'var(--color-bg-elevated)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <Space>
                      <Tag color="blue">主</Tag>
                      <Typography.Text>
                        {ownerRow
                          ? ownerRow.account_name || userNameOf(ownerRow.account_id)
                          : data.primary_account_id
                            ? userNameOf(data.primary_account_id)
                            : '未指派'}
                      </Typography.Text>
                      {ownerRow?.account_email && (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 'var(--font-size-xs)' }}
                        >
                          {ownerRow.account_email}
                        </Typography.Text>
                      )}
                    </Space>
                    {canReassign ? (
                      <Button size="small" onClick={() => setReassignOpen(true)}>
                        改派
                      </Button>
                    ) : (
                      <Tooltip title="仅租户管理员 / 平台运营方可改派">
                        <Button size="small" disabled>
                          改派
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Assistants */}
                <div>
                  <Typography.Text strong style={{ fontSize: 'var(--font-size-sm)' }}>
                    协助者 (Assistants)
                  </Typography.Text>
                  <List
                    size="small"
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无协助者" /> }}
                    dataSource={assistantRows}
                    renderItem={(r) => (
                      <List.Item
                        actions={
                          canManageAssistants
                            ? [
                                <Popconfirm
                                  key="rm"
                                  title="移除该协助者?"
                                  okText="移除"
                                  cancelText="取消"
                                  onConfirm={() => removeAssistantMut.mutate(r.account_id)}
                                >
                                  <Button
                                    size="small"
                                    danger
                                    type="link"
                                    loading={
                                      removeAssistantMut.isPending &&
                                      removeAssistantMut.variables === r.account_id
                                    }
                                  >
                                    移除
                                  </Button>
                                </Popconfirm>,
                              ]
                            : []
                        }
                      >
                        <Space>
                          <span>{r.account_name || userNameOf(r.account_id)}</span>
                          {r.account_email && (
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 'var(--font-size-xs)' }}
                            >
                              {r.account_email}
                            </Typography.Text>
                          )}
                          {r.notes && (
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 'var(--font-size-xs)' }}
                            >
                              · {r.notes}
                            </Typography.Text>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>

                {/* + 添加协助者 */}
                {canManageAssistants && (
                  <div
                    style={{
                      borderTop: '1px dashed var(--color-border-soft)',
                      paddingTop: 12,
                    }}
                  >
                    <Typography.Text strong style={{ fontSize: 'var(--font-size-sm)' }}>
                      + 添加协助者
                    </Typography.Text>
                    <Form
                      form={addAssistantForm}
                      layout="vertical"
                      style={{ marginTop: 6 }}
                      onFinish={(vals) => {
                        addAssistantMut.mutate({
                          account_id: vals.account_id,
                          notes: vals.notes?.trim() || undefined,
                        });
                      }}
                    >
                      <Form.Item
                        name="account_id"
                        rules={[{ required: true, message: '请选择账号' }]}
                      >
                        <Select
                          showSearch
                          placeholder="选择账号"
                          loading={usersQuery.isLoading}
                          options={userOptions
                            .filter(
                              (o) =>
                                o.value !== data.primary_account_id &&
                                !assistantRows.some((r) => r.account_id === o.value),
                            )
                            .map((o) => ({
                              value: o.value,
                              label: `${o.label} (${o.role})`,
                              searchValue: `${o.label} ${o.role}`,
                            }))}
                          optionFilterProp="searchValue"
                          filterOption={(input, option) =>
                            !!(option as { searchValue?: string })?.searchValue
                              ?.toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        />
                      </Form.Item>
                      <Form.Item name="notes">
                        <Input placeholder="备注(选填)" maxLength={200} />
                      </Form.Item>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={addAssistantMut.isPending}
                          size="small"
                        >
                          添加
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>
                )}

                {!canManageAssistants && (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'var(--font-size-xs)' }}
                  >
                    仅案主负责人 / 租户管理员可加减协助者
                  </Typography.Text>
                )}
              </>
            )}
          </Space>
        )}
      </Modal>

      {/* 改派 子 Modal */}
      <Modal
        title="改派负责人"
        open={reassignOpen}
        onCancel={() => setReassignOpen(false)}
        onOk={async () => {
          try {
            const v = await reassignForm.validateFields();
            reassignMut.mutate({
              owner_account_id: v.owner_account_id,
              note: v.note?.trim() || undefined,
            });
          } catch {
            /* noop */
          }
        }}
        okText="确认改派"
        cancelText="取消"
        confirmLoading={reassignMut.isPending}
        destroyOnClose
      >
        <Form form={reassignForm} layout="vertical">
          <Form.Item
            name="owner_account_id"
            label="新负责人"
            rules={[{ required: true, message: '请选择新负责人' }]}
          >
            <Select
              showSearch
              placeholder="选择新负责人"
              loading={usersQuery.isLoading}
              options={userOptions
                .filter((o) => o.value !== data?.primary_account_id)
                .map((o) => ({
                  value: o.value,
                  label: `${o.label} (${o.role})`,
                  searchValue: `${o.label} ${o.role}`,
                }))}
              optionFilterProp="searchValue"
              filterOption={(input, option) =>
                !!(option as { searchValue?: string })?.searchValue
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="note" label="说明 (选填)" extra="审计日志会保留改派原因">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
